/**
 * Post a plan-review summary card into the project's Design group comment thread.
 */

import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  collectGroupCommentContext,
  normalizeGroupName,
} from './groupComments';
import type {
  ProjectOnboarding,
  ProjectTask,
  TaskGroup,
  TimberProject,
} from '../types';

export interface PostPlanReviewDesignCommentParams {
  reviewId: string;
  projectId: string;
  authorId: string;
  authorName: string;
  fileName: string;
  commentCount: number;
  taskGroups: TaskGroup[];
  timberProjects: TimberProject[];
  projectTasks: ProjectTask[];
  projectOnboardings?: ProjectOnboarding[];
}

/** Stable taskComments id so the browser and the completion function share one card. */
export function planReviewDesignCommentDocId(reviewId: string): string {
  return `planReview_${reviewId.trim()}`;
}

/** Design group on the file's project. A deal sibling is used only when this project has none. */
export function findDesignGroupForProject(
  projectId: string,
  taskGroups: TaskGroup[],
  timberProjects: TimberProject[],
): TaskGroup | null {
  const pid = (projectId || '').trim();
  if (!pid) return null;

  const own = taskGroups.find(
    (g) => g.projectId === pid && g.id && normalizeGroupName(g.name) === 'design',
  );
  if (own) return own;

  const project = timberProjects.find((p) => p.id === pid);
  const dealId = project?.dealId?.trim() || '';
  if (!dealId) return null;

  const siblingIds = timberProjects
    .filter((p) => p.id && p.id !== pid && p.dealId?.trim() === dealId)
    .map((p) => p.id!);
  for (const id of siblingIds) {
    const group = taskGroups.find(
      (g) => g.projectId === id && g.id && normalizeGroupName(g.name) === 'design',
    );
    if (group) return group;
  }
  return null;
}

export async function postPlanReviewDesignGroupComment(
  params: PostPlanReviewDesignCommentParams,
): Promise<string | null> {
  const reviewId = (params.reviewId || '').trim();
  const projectId = (params.projectId || '').trim();
  if (!reviewId || !projectId || !params.authorId) return null;

  const designGroup = findDesignGroupForProject(
    projectId,
    params.taskGroups,
    params.timberProjects,
  );
  if (!designGroup?.id) {
    console.warn('[postPlanReviewDesignGroupComment] No Design group for project', projectId);
    return null;
  }

  const canonicalRef = doc(db, 'taskComments', planReviewDesignCommentDocId(reviewId));
  const canonicalSnap = await getDoc(canonicalRef);
  if (canonicalSnap.exists() && String(canonicalSnap.data()?.projectId || '') === designGroup.projectId) {
    return canonicalRef.id;
  }

  const currentProject = params.timberProjects.find((p) => p.id === designGroup.projectId);
  const ctx = collectGroupCommentContext(
    designGroup,
    currentProject,
    params.timberProjects,
    params.taskGroups,
    params.projectTasks,
    params.projectOnboardings || [],
  );

  const body =
    `Completed plan review on "${params.fileName}"` +
    (params.commentCount > 0
      ? ` — ${params.commentCount} comment${params.commentCount === 1 ? '' : 's'}`
      : '') +
    '.';

  await setDoc(canonicalRef, {
    taskId: '',
    groupCommentKey: ctx.groupKey,
    groupId: ctx.primaryGroupId,
    projectId: ctx.primaryProjectId,
    notifyUserIds: ctx.notifyUserIds.length > 0 ? ctx.notifyUserIds : null,
    authorId: params.authorId,
    authorName: params.authorName,
    body,
    bodyFormat: 'plain',
    source: 'plan_review',
    planReviewId: reviewId,
    planReviewFileName: params.fileName,
    kind: 'note',
    createdAt: canonicalSnap.exists()
      ? canonicalSnap.data()?.createdAt || Timestamp.now()
      : Timestamp.now(),
  });
  return canonicalRef.id;
}
